import type { CodingSessionView } from './coding-events';
import { cn } from '~/lib/utils';

/**
 * FRONT-3146: the new-build screen's left column — the person's existing builds
 * as a centered vertical timeline, newest first, grouped Today / This week /
 * Earlier. Pure: props in, no fetching. One click on an entry opens that build.
 */

export type TimelineSession = Pick<
  CodingSessionView,
  'id' | 'title' | 'updatedAt' | 'status' | 'runLive' | 'awaitingAnswer' | 'lastRunEndedAt' | 'lastRunOutcome'
> & { lastActivityAt?: string | null };

export type BuildStatus = 'running' | 'waiting' | 'done' | 'failed';
export type TimelineBucket = 'Today' | 'This week' | 'Earlier';

const DAY_MS = 86_400_000;

/** Existing vocabulary: running / waiting (on your answer) / done / failed. A
 *  build that never ran carries no status. `status === 'running'` alone is not
 *  live (stale lease), so `runLive` decides. */
export function buildStatusOf(s: TimelineSession): BuildStatus | null {
  if (s.runLive) return s.awaitingAnswer ? 'waiting' : 'running';
  if (s.status === 'failed' || s.lastRunOutcome === 'failed') return 'failed';
  if (s.lastRunEndedAt) return 'done';
  return null;
}

function activityMs(s: TimelineSession): number {
  const t = Date.parse(s.lastActivityAt || s.updatedAt);
  return Number.isNaN(t) ? 0 : t;
}

export function bucketOf(ms: number, now: number): TimelineBucket {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  if (ms >= startOfToday.getTime()) return 'Today';
  if (ms >= now - 7 * DAY_MS) return 'This week';
  return 'Earlier';
}

/** Short date: time today, weekday this week, otherwise day + month. */
export function shortDate(ms: number, now: number): string {
  if (!ms) return 'Unknown date';
  const d = new Date(ms);
  const bucket = bucketOf(ms, now);
  if (bucket === 'Today') return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (bucket === 'This week') return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const STATUS: Record<BuildStatus, { label: string; dot: string; chip: string }> = {
  running: {
    label: 'Running',
    dot: 'bg-primary',
    chip: 'border-primary/30 bg-primary/10 text-primary',
  },
  waiting: {
    label: 'Waiting for you',
    dot: 'bg-amber-500',
    chip: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  },
  done: {
    label: 'Done',
    dot: 'bg-success',
    chip: 'border-success/30 bg-success/10 text-success',
  },
  failed: {
    label: 'Failed',
    dot: 'bg-destructive',
    chip: 'border-destructive/30 bg-destructive/10 text-destructive',
  },
};

const BUCKET_ORDER: TimelineBucket[] = ['Today', 'This week', 'Earlier'];

export function groupTimeline(
  sessions: TimelineSession[],
  now: number,
): Array<{ bucket: TimelineBucket; items: Array<{ session: TimelineSession; ms: number }> }> {
  const sorted = sessions
    .map((session) => ({ session, ms: activityMs(session) }))
    .sort((a, b) => b.ms - a.ms);
  return BUCKET_ORDER.map((bucket) => ({
    bucket,
    items: sorted.filter((e) => bucketOf(e.ms, now) === bucket),
  })).filter((g) => g.items.length > 0);
}

/** The list's width, in px — the Build window floats it beside a centred column when there is room (FRONT-3223). */
export const BUILDS_LIST_PX = 340;

/**
 * Whether the list can float over the window's left edge without covering a
 * centred reading column: room for the column plus the list on both sides. A
 * "Full" reading width (no cap) never leaves that room.
 */
export function buildsListFloats(columnMax: number | 'none', windowWidth: number): boolean {
  return typeof columnMax === 'number' && windowWidth >= columnMax + 2 * BUILDS_LIST_PX;
}

export function ExistingBuildsTimeline({
  sessions,
  onOpen,
  selectedId,
  className,
  now = Date.now(),
  mirroredEyebrow = false,
}: {
  sessions: TimelineSession[];
  onOpen: (id: string) => void;
  /** FRONT-3138: the build open beside the rail, marked as current. */
  selectedId?: string | null;
  className?: string;
  /** Injectable clock so bucketing is deterministic in tests. */
  now?: number;
  /**
   * FRONT-3223 (eddy, 2026-10-28): the reworked Build window's list opens with
   * the right column's top line mirrored — "CODING SESSION · LOAD PROJECT"
   * against the divider, its rule toward it — instead of "Your builds".
   */
  mirroredEyebrow?: boolean;
}) {
  if (sessions.length === 0) return null;
  const groups = groupTimeline(sessions, now);
  return (
    <nav
      aria-label="Your builds"
      data-testid="existing-builds"
      // FRONT-3138: its own scroller, so the rail and the conversation beside
      // it scroll independently.
      className={cn('h-full w-[340px] shrink-0 overflow-y-auto px-5 py-6', className)}
    >
      {mirroredEyebrow ? (
        <h2
          data-testid="existing-builds-eyebrow"
          className="mb-5 flex items-center justify-end gap-2.5 text-[length:var(--text-xs)] font-medium uppercase tracking-[0.08em] text-muted-foreground"
        >
          <span className="font-semibold text-primary">Coding session</span>
          <span aria-hidden className="text-muted-foreground/60">·</span>
          <span>Load project</span>
          <span aria-hidden className="h-px bg-primary" style={{ width: 22 }} />
        </h2>
      ) : (
        <h2 className="mb-5 text-center text-[length:var(--text-xs)] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Your builds
        </h2>
      )}
      <div className="relative">
        <div aria-hidden="true" className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
        {/* FRONT-3138: the line ends in a dot, so the oldest build reads as
            the end of the list rather than a line running off. */}
        <span
          aria-hidden="true"
          data-testid="timeline-end"
          className="absolute bottom-0 left-1/2 size-2 -translate-x-1/2 translate-y-1/2 rounded-full bg-border"
        />
        {groups.map((g) => (
          <section key={g.bucket} aria-label={g.bucket} className="relative">
            <h3 className="relative mb-3 flex justify-center">
              <span className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[length:var(--text-xs)] text-muted-foreground">
                {g.bucket}
              </span>
            </h3>
            <ol className="flex flex-col gap-3 pb-4">
              {g.items.map(({ session: s, ms }) => {
                const status = buildStatusOf(s);
                const title = s.title || 'Untitled build';
                const date = shortDate(ms, now);
                const current = s.id === selectedId;
                return (
                  <li key={s.id} className="relative pt-1.5">
                    <span
                      aria-hidden="true"
                      data-testid="timeline-node"
                      className={cn(
                        'absolute left-1/2 top-0 z-10 size-3 -translate-x-1/2 rounded-full ring-4 ring-background',
                        status ? STATUS[status].dot : 'bg-muted-foreground/40',
                        status === 'running' && 'motion-safe:animate-pulse',
                      )}
                    />
                    <button
                      type="button"
                      data-testid="existing-build"
                      data-status={status ?? undefined}
                      aria-current={current ? 'page' : undefined}
                      aria-label={`${title}, ${date}${status ? `, ${STATUS[status].label}` : ''}`}
                      onClick={() => onOpen(s.id)}
                      className={cn(
                        'group relative flex w-full flex-col items-center gap-1 rounded-xl border bg-card px-3 pb-2.5 pt-3.5 text-center transition-[border-color,box-shadow,transform] duration-150 hover:border-foreground/25 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-safe:hover:-translate-y-px',
                        current ? 'border-primary/50 bg-primary/5' : 'border-border',
                      )}
                    >
                      <span className="line-clamp-2 w-full text-[length:var(--text-sm)] font-medium text-foreground">
                        {title}
                      </span>
                      <span className="flex items-center gap-2 text-[length:var(--text-xs)] text-muted-foreground">
                        <span>{date}</span>
                        {status && (
                          <span
                            data-testid="timeline-status"
                            className={cn('rounded-full border px-1.5 py-px', STATUS[status].chip)}
                          >
                            {STATUS[status].label}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </nav>
  );
}
